import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import type { RiskCatalogItem } from './types';

interface CatalogMergeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sourceItem: RiskCatalogItem | null;
  candidates: RiskCatalogItem[];
  onSubmit: (toId: string) => Promise<void>;
}

export function CatalogMergeModal({
  open,
  onOpenChange,
  sourceItem,
  candidates,
  onSubmit,
}: CatalogMergeModalProps) {
  const [toId, setToId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectable = useMemo(
    () => candidates.filter((item) => item.id !== sourceItem?.id && item.active),
    [candidates, sourceItem?.id],
  );

  useEffect(() => {
    if (!open) return;
    setToId(selectable[0]?.id ?? '');
    setError(null);
  }, [open, selectable]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!toId) {
      setError('Seleccione el ítem destino.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSubmit(toId);
      onOpenChange(false);
    } catch {
      setError('No se pudo fusionar. Verifique que ambos ítems sean del mismo tipo.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Fusionar ítem de catálogo</DialogTitle>
        </DialogHeader>
        {sourceItem && (
          <p className="text-sm text-muted-foreground">
            Las referencias de{' '}
            <span className="font-medium text-foreground">{sourceItem.label}</span> se moverán al
            ítem destino. El ítem origen quedará desactivado.
          </p>
        )}
        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1" htmlFor="merge-target-id">
              Ítem destino
            </label>
            {selectable.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No hay otro ítem activo en este catálogo para usar como destino.
              </p>
            ) : (
              <select
                id="merge-target-id"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={toId}
                onChange={(e) => setToId(e.target.value)}
                required
              >
                <option value="">Seleccionar ítem…</option>
                {selectable.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.label}
                  </option>
                ))}
              </select>
            )}
            <p className="mt-1 text-xs text-muted-foreground">
              Referencia CAT-05: reapunta riesgos operativos al ítem elegido.
            </p>
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => onOpenChange(false)}
              disabled={saving}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving || selectable.length === 0 || !toId}
            >
              {saving ? 'Fusionando…' : 'Fusionar'}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
